import { useParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BlogCard from '../../components/base/BlogCard';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import { categories, allPosts, recentPosts } from '../../mocks/blog-posts';

export default function CategoryPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const { slug } = useParams();
  const category = categories.find(cat => cat.slug === slug);
  
  if (!category) {
    return <div>Categoria não encontrada</div>;
  }

  const categoryPosts = [...allPosts, ...recentPosts].filter(
    post => post.category === category.name
  );

  // Conteúdo específico para Ativo Fixo & Patrimônio
  const isAtivoFixo = slug === 'ativo-fixo-patrimonio';

  const integrationPoints = [
    {
      icon: 'ri-database-2-line',
      title: 'Cadastro Centralizado',
      description: 'Sistema único de registro com informações completas: dados fiscais, contábeis, localização física e responsáveis.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Depreciação Automatizada',
      description: 'Cálculo automático de depreciação contábil e fiscal, com múltiplos métodos e geração de relatórios gerenciais.'
    },
    {
      icon: 'ri-qr-code-line',
      title: 'Rastreamento por RFID/QR',
      description: 'Identificação única de cada ativo com tecnologia RFID ou QR Code para inventários rápidos e precisos.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Inventário Integrado',
      description: 'Processo de inventário físico conectado ao sistema contábil, com conciliação automática e ajustes em tempo real.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Compliance Fiscal',
      description: 'Conformidade com IFRS, CPC 27, Sped Fiscal e demais obrigações acessórias de forma automatizada.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Análise de Performance',
      description: 'Dashboards com indicadores de ROI, taxa de utilização, custos de manutenção e vida útil dos ativos.'
    }
  ];

  const bestPractices = [
    {
      number: '01',
      title: 'Política de Capitalização Clara',
      description: 'Defina critérios objetivos para capitalização de ativos: valor mínimo, vida útil esperada e natureza do bem.'
    },
    {
      number: '02',
      title: 'Revisão Periódica de Vida Útil',
      description: 'Reavalie anualmente a vida útil e valor residual dos ativos para garantir depreciação adequada.'
    },
    {
      number: '03',
      title: 'Inventário Físico Regular',
      description: 'Realize inventários físicos pelo menos uma vez ao ano, com conciliação imediata das divergências.'
    },
    {
      number: '04',
      title: 'Segregação de Funções',
      description: 'Separe as responsabilidades de custódia física, registro contábil e autorização de movimentações.'
    },
    {
      number: '05',
      title: 'Documentação Completa',
      description: 'Mantenha toda documentação: notas fiscais, contratos, laudos de avaliação e registros de manutenção.'
    },
    {
      number: '06',
      title: 'Integração com ERP',
      description: 'Integre o sistema de ativos fixos com ERP, contabilidade e compras para fluxo de dados automático.'
    }
  ];

  return (
    <>
      <SEOHead
        title={`${category.name} | Blog Contábil`}
        description={`${category.description} - ${category.articleCount} artigos publicados sobre ${category.name.toLowerCase()}.`}
        keywords={`${category.name.toLowerCase()}, artigos contábeis, blog contabilidade`}
        canonical={`${siteUrl}/categoria/${category.slug}`}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `${siteUrl}/categoria/${category.slug}`,
            },
          ],
        }}
      />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-[#F8FAFC] to-[#EFF6FF]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#1E40AF]/10"
            >
              <i className={`${category.icon} text-4xl text-[#1E40AF]`}></i>
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#0F172A] mb-3">{category.name}</h1>
              <p className="text-xl text-[#475569]">{category.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#64748B] mt-6">
            <span className="flex items-center gap-2">
              <i className="ri-article-line text-[#1E40AF]"></i>
              {category.articleCount} artigos publicados
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-refresh-line text-[#1E40AF]"></i>
              Atualizado semanalmente
            </span>
          </div>
        </div>
      </section>

      {/* Gestão Integrada Section - Only for Ativo Fixo */}
      {isAtivoFixo && (
        <>
          <section className="py-20 px-6 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-[#0F172A] mb-4">
                  Gestão Integrada de Ativos Fixos
                </h2>
                <p className="text-lg text-[#475569] max-w-3xl mx-auto">
                  Uma gestão eficiente de ativos fixos requer integração entre processos contábeis, fiscais, operacionais e tecnológicos. Conheça os principais pilares:
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {integrationPoints.map((point, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-[#F8FAFC] to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1E40AF]/10 mb-6 group-hover:bg-[#1E40AF] transition-all duration-300">
                      <i className={`${point.icon} text-2xl text-[#1E40AF] group-hover:text-white transition-all duration-300`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                      {point.title}
                    </h3>
                    <p className="text-[#475569] leading-relaxed">
                      {point.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Best Practices Section */}
          <section className="py-20 px-6 bg-gradient-to-br from-[#F8FAFC] to-[#EFF6FF]">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-[#0F172A] mb-4">
                  Melhores Práticas de Controle
                </h2>
                <p className="text-lg text-[#475569] max-w-3xl mx-auto">
                  Implemente estas práticas essenciais para garantir controle efetivo, conformidade e otimização do seu patrimônio:
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {bestPractices.map((practice, index) => (
                  <div 
                    key={index}
                    className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300 flex gap-6"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-[#1E40AF] text-white text-2xl font-bold">
                        {practice.number}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                        {practice.title}
                      </h3>
                      <p className="text-[#475569] leading-relaxed">
                        {practice.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-20 px-6 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A] rounded-3xl p-12 text-white">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-4xl font-bold mb-6">
                      Benefícios da Gestão Integrada
                    </h2>
                    <p className="text-lg text-white/90 mb-8">
                      Uma gestão integrada de ativos fixos traz resultados mensuráveis para sua organização:
                    </p>
                    <div className="space-y-4">
                      {[
                        'Redução de até 30% nos custos operacionais',
                        'Conformidade fiscal e contábil garantida',
                        'Inventários 80% mais rápidos e precisos',
                        'Decisões estratégicas baseadas em dados reais',
                        'Otimização do ciclo de vida dos ativos',
                        'Eliminação de perdas e extravios'
                      ].map((benefit, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20">
                            <i className="ri-check-line text-sm"></i>
                          </div>
                          <span className="text-white/95">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="space-y-6">
                      <div>
                        <div className="text-5xl font-bold mb-2">98%</div>
                        <div className="text-white/80">Acurácia no inventário</div>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <div className="text-5xl font-bold mb-2">60%</div>
                        <div className="text-white/80">Redução no tempo de fechamento</div>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <div className="text-5xl font-bold mb-2">100%</div>
                        <div className="text-white/80">Conformidade regulatória</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Articles Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-3">
              Artigos sobre {category.name}
            </h2>
            <p className="text-[#475569]">
              Conteúdo técnico e atualizado para profissionais da área
            </p>
          </div>

          {categoryPosts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categoryPosts.map((post) => (
                  <BlogCard key={post.id} {...post} />
                ))}
              </div>

              <div className="flex justify-center mt-12">
                <div className="flex items-center gap-2">
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-[#475569] hover:bg-[#1E40AF] hover:text-white hover:border-[#1E40AF] transition-all cursor-pointer">
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1E40AF] text-white font-semibold cursor-pointer">
                    1
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-[#475569] hover:bg-[#1E40AF] hover:text-white hover:border-[#1E40AF] transition-all cursor-pointer">
                    2
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-[#475569] hover:bg-[#1E40AF] hover:text-white hover:border-[#1E40AF] transition-all cursor-pointer">
                    3
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-[#475569] hover:bg-[#1E40AF] hover:text-white hover:border-[#1E40AF] transition-all cursor-pointer">
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-[#64748B]">Em breve novos artigos nesta categoria</p>
            </div>
          )}
        </div>
      </section>

      {/* Other Categories Section */}
      <section className="py-16 px-6 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8">Outras Categorias</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.filter(cat => cat.slug !== slug).map((cat) => (
              <a
                key={cat.id}
                href={`/categoria/${cat.slug}`}
                className="group bg-white rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer"
              >
                <div 
                  className="w-12 h-12 flex items-center justify-center rounded-lg mb-4 bg-[#1E40AF]/10"
                >
                  <i className={`${cat.icon} text-2xl text-[#1E40AF]`}></i>
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#1E40AF] transition-colors">
                  {cat.name}
                </h3>
                <p className="text-sm text-[#64748B] mb-3">{cat.description}</p>
                <span className="text-xs text-[#1E40AF] font-medium">{cat.articleCount} artigos</span>
              </a>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
}